import React, { Component } from "react";
import { t } from "../../../components/CustomComponent.js";
import HomeMedia from "./HomeMedia.js";
import ICON from "../icons.js";

/* ============================================================================
 * PropertyGallery — แกลเลอรีรูปของหน้ารายละเอียดทรัพย์
 *
 *   จอใหญ่ : รูปปกใบใหญ่ซ้าย + ตาราง 2x2 ด้านขวา ใบสุดท้ายกดดูรูปทั้งหมด
 *   จอเล็ก : เหลือรูปเดียวเลื่อนซ้าย-ขวา (ตาราง 2x2 ซ่อนด้วย CSS)
 *   กดที่รูป : เปิดดูเต็มจอ เลื่อนด้วยปุ่ม / ลูกศรซ้าย-ขวา / ปิดด้วย Esc
 *
 * รูปยังเป็นภาพจำลอง (HomeMedia) — เมื่อมีไฟล์จริงให้ส่ง photo.url เข้ามา
 * คอมโพเนนต์จะ render <img> ให้เอง
 * ========================================================================== */
const SIDE_COUNT = 4; // จำนวนรูปย่อยด้านขวาของรูปปก

export default class PropertyGallery extends Component {
  state = { index: 0, open: false };

  componentDidMount() {
    window.addEventListener("keydown", this.onKey);
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.onKey);
    document.body.classList.remove("hm-pd-noscroll");
  }

  onKey = (event) => {
    if (!this.state.open) return;
    if (event.key === "Escape") this.close();
    if (event.key === "ArrowRight") this.step(1);
    if (event.key === "ArrowLeft") this.step(-1);
  };

  photos = () => this.props.photos || [];

  /* เลื่อนแบบวน — ถึงรูปสุดท้ายแล้วกดต่อจะกลับมารูปแรก */
  step = (delta) => {
    const total = this.photos().length;
    if (!total) return;
    this.setState((state) => ({ index: (state.index + delta + total) % total }));
  };

  open = (index) => {
    document.body.classList.add("hm-pd-noscroll"); // กันหน้าเว็บด้านหลังเลื่อนตาม
    this.setState({ index, open: true });
  };

  close = () => {
    document.body.classList.remove("hm-pd-noscroll");
    this.setState({ open: false });
  };

  /* กล่องรูป 1 ใบ — ใช้ทั้งรูปปกและรูปย่อย */
  cell = (item, index, className, ratio) => (
    <button
      key={item.uid}
      type="button"
      className={`hm-pd-gallery__cell ${className}`}
      onClick={() => this.open(index)}
      aria-label={t(item.room)}
    >
      <HomeMedia kind={item.kind} tone={item.tone} ratio={ratio} src={item.url} alt={t(item.room)} />
    </button>
  );

  render() {
    const { status, deals = [], badge, children } = this.props;
    const { index, open } = this.state;
    const photos = this.photos();

    if (!photos.length) return null;

    const cover = photos[0];
    const side = photos.slice(1, 1 + SIDE_COUNT);
    const rest = photos.length - (1 + side.length);
    const current = photos[index] || cover;

    return (
      <div className="hm-pd-gallery">
        <div className="hm-pd-gallery__grid">
          {/* ---------------------------------------------------- รูปปกใบใหญ่ */}
          <div className="hm-pd-gallery__main">
            {this.cell(cover, 0, "hm-pd-gallery__cell--main", 0.66)}

            {/* ป้ายสถานะ + ประเภทประกาศ ทับมุมบนซ้าย */}
            <div className="hm-pd-gallery__badges">
              {!!status && <span className={`hm-pd-status is-${status}`}>{t(`status_${status}`)}</span>}
              {deals.map((deal) => (
                <span key={deal} className="hm-badge hm-badge--light">
                  {t(deal === "rent" ? "for_rent" : "for_sale")}
                </span>
              ))}
              {!!badge && <span className="hm-badge hm-badge--orange">{t(badge)}</span>}
            </div>

            {/* ปุ่มเลื่อนรูป — จอเล็กใช้เลื่อนดูรูปในกรอบเดียว */}
            <button
              type="button"
              className="hm-pd-gallery__nav is-prev"
              aria-label={t("previous")}
              onClick={() => this.step(-1)}
            >
              <span className="hm-ico">{ICON.chevron_left}</span>
            </button>
            <button
              type="button"
              className="hm-pd-gallery__nav is-next"
              aria-label={t("next")}
              onClick={() => this.step(1)}
            >
              <span className="hm-ico">{ICON.chevron_right}</span>
            </button>

            <span className="hm-pd-gallery__count">
              {index + 1} / {photos.length}
            </span>

            {children}
          </div>

          {/* ------------------------------------------------ รูปย่อย 2x2 ขวา */}
          <div className="hm-pd-gallery__side">
            {side.map((item, i) => {
              const isLast = i === side.length - 1 && rest > 0;
              return (
                <div key={item.uid} className="hm-pd-gallery__slot">
                  {this.cell(item, i + 1, "hm-pd-gallery__cell--side", 0.68)}
                  {isLast && (
                    <button type="button" className="hm-pd-gallery__more" onClick={() => this.open(i + 1)}>
                      <span className="hm-pd-gallery__more-num">+{rest}</span>
                      <span className="hm-pd-gallery__more-text">{t("detail_view_all_photos")}</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ------------------------------------------------------- ดูเต็มจอ */}
        {open && (
          <div className="hm-pd-lightbox" role="dialog" aria-modal="true">
            <div className="hm-pd-lightbox__bar">
              <span className="hm-pd-lightbox__count">
                {index + 1} / {photos.length}
                <span className="hm-pd-lightbox__room">{t(current.room)}</span>
              </span>
              <button type="button" className="hm-pd-lightbox__close" aria-label={t("close")} onClick={this.close}>
                <span className="hm-ico">{ICON.close}</span>
              </button>
            </div>

            <div className="hm-pd-lightbox__stage">
              <button
                type="button"
                className="hm-pd-lightbox__nav is-prev"
                aria-label={t("previous")}
                onClick={() => this.step(-1)}
              >
                <span className="hm-ico">{ICON.chevron_left}</span>
              </button>

              <div className="hm-pd-lightbox__frame">
                <HomeMedia
                  kind={current.kind}
                  tone={current.tone}
                  ratio={0.62}
                  src={current.url}
                  alt={t(current.room)}
                />
              </div>

              <button
                type="button"
                className="hm-pd-lightbox__nav is-next"
                aria-label={t("next")}
                onClick={() => this.step(1)}
              >
                <span className="hm-ico">{ICON.chevron_right}</span>
              </button>
            </div>

            <div className="hm-pd-lightbox__strip">
              {photos.map((item, i) => (
                <button
                  key={item.uid}
                  type="button"
                  className={`hm-pd-lightbox__thumb ${i === index ? "is-on" : ""}`}
                  onClick={() => this.setState({ index: i })}
                  aria-label={`${i + 1}`}
                >
                  <HomeMedia kind={item.kind} tone={item.tone} ratio={0.7} src={item.url} alt={t(item.room)} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
}
